import { useCallback, useMemo, useState, type CSSProperties } from "react";
import { notifySuccess } from "@marekl/adminer/api";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ActionBar,
  Alert,
  Badge,
  Button,
  Card,
  DatePicker,
  DescriptionList,
  EmptyState,
  ErrorState,
  FilterField,
  FilterRow,
  FormSectionLabel,
  FormStack,
  Input,
  LoadingState,
  Page,
  ResponsiveGrid,
  Select,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { toApiDate, type Dayjs } from "@marekl/adminer/datetime";
import { apiErrorMessage } from "@marekl/adminer/api";
import { useRowSelection, useT, useTableSort } from "@marekl/adminer/hooks";
import { formatDateTime } from "@marekl/adminer/format";
import dayjs from "dayjs";
import { Check, ChevronRight } from "lucide-react";
import { AppTopbar } from "../../../shell/AppTopbar";
import { AgeBadge } from "../../../components/AgeBadge";
import { RegisteredTick } from "../../../components/RegisteredTick";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import {
  useCompetition,
  useCompetitionOffer,
  type Competition,
} from "../../../lib/queries/competitions";
import { familyName, useRuleSetDocument, type RuleSetDocument } from "../../../lib/queries/rules";
import { canEditEntry, ENTRY_LOCK_MESSAGES } from "../../../lib/entryWindow";
import { daysLeftLabel, formatCompetitionDates } from "../../../lib/competitionState";
import {
  competitorMayEnter,
  hasRegisteredMember,
  needsSeasonRegistration,
  teamMayEnter,
  SEASON_REGISTRATION_BLOCKED,
  seasonRegistrationAccess,
} from "../../../lib/entryEligibility";
import { canManageCompetition, useMe } from "../../../auth/queries";
import { useStartList } from "../../../lib/queries/startlists";
import { useSeason } from "../../../season";
import { useTeam } from "../../../lib/queries/teams";
import { useAddTeamMember, useTeamMembers, type Membership } from "../../../lib/queries/memberships";
import {
  entryErrorRows,
  useCreateEntry,
  useTeamEntries,
  useUpdateEntry,
  type Entry,
} from "../../../lib/queries/entries";
import {
  indexDocument,
  pickComboForAge,
  type ComboRef,
  type DocumentIndex,
} from "../../../lib/rulesDocument";
import {
  ageCategoryAdmits,
  ageCategoryMatches,
  ageOn,
  ageReferenceLabel,
  aggregateAge,
  formatAge,
  headcountBound,
  headcountFits,
} from "../../../lib/entryChecks";
import {
  AGE_AGGREGATION_LABELS,
  formatSeconds as formatTimeBound,
} from "../../../lib/rulesLabels";
import { MusicPanel } from "../../../components/MusicPanel";
import { entryMusicUrl, useUploadEntryMusic, type EntryMusic } from "../../../lib/queries/music";
import styles from "./EntryWizardPage.module.css";
import type { ID } from "../../../lib/id";

// EntryWizardPage is THE surface for composing a přihláška — creating
// and editing, for leaders and for managers adding on a team's behalf:
// three steps (combination, competitors, summary) with room for a
// proper roster table. The route without :entryId creates; with it, the
// same wizard edits that entry.
//
// A new přihláška for a discipline danced to the club's own music gets a
// fourth step. It appears only AFTER the entry has been submitted,
// because there is nothing to attach a recording to until then — which
// is also why it is not a gate: the spec allows entering without music
// and reminding until the uzávěrka hudby, so the step offers the upload
// and lets the leader walk away from it.

const STEP_LABELS = ["Kombinace", "Soutěžící", "Shrnutí"];
const MUSIC_STEP = 3;

export function EntryWizardPage() {
  const { id, competitionId, entryId } = useParams();
  const teamId = id ?? "";
  const compId = competitionId ?? "";
  const editedId = entryId || undefined;

  const [restartNonce, setRestartNonce] = useState(0);

  const { data: competition, isLoading, error, refetch } = useCompetition(compId);
  // „Zveřejněno" closes new přihlášky to the club (R16); a club reading a
  // list that is still a draft gets 404, which here just means „not yet".
  const { data: startList } = useStartList(compId);
  const { data: viewer } = useMe();
  const {
    data: document,
    error: documentError,
    refetch: refetchDocument,
  } = useRuleSetDocument(competition?.rule_set_id);
  const { data: members = [], isLoading: membersLoading } = useTeamMembers(teamId);
  const { data: team, isLoading: teamLoading, error: teamError, refetch: refetchTeam } =
    useTeam(teamId);
  // The guards below wait for the team and refuse to render without it, so
  // the fallback is unreachable. It is not a formality: this value decides
  // whether MČR is this club's competition at all (teamMayEnter) and which
  // kind „Přidat soutěžícího" creates — reading an unloaded team as ČNSMS let
  // the wizard open for a club it refuses, and post `guest` on a roster that
  // may hold only `foreign`, which the server then rejects on a field the
  // user never touched. The keyed inner component also captures teamCountry
  // into its initial state, so an answer arriving later never reseeds it.
  const teamIsCmf = team?.federation_is_primary ?? true;
  const { data: entries, isLoading: entriesLoading } = useTeamEntries(
    editedId ? teamId : undefined,
    compId,
  );
  const index = useMemo(() => (document ? indexDocument(document) : null), [document]);
  const entry = editedId ? entries?.find((e) => e.id === editedId) : undefined;

  const crumbs = [
    { label: "Členové týmu", href: `/teams/${teamId}` },
    { label: "Soutěže", href: `/teams/${teamId}/souteze` },
    { label: competition?.name ?? "Soutěž", href: `/teams/${teamId}/souteze/${compId}` },
    { label: editedId ? "Upravit přihlášku" : "Nová přihláška" },
  ];

  // Errors first: with a failed competition (or document) fetch the
  // document never loads, so a loading-first guard would spin forever
  // and the retry surface would be unreachable.
  //
  // The team is in here too, and not only for the retry: a failed team fetch
  // clears teamLoading with `team` still undefined, and `?? true` then reads
  // a club outside ČNSMS as ČNSMS — opening the MČR wizard for a club it refuses
  // and posting `kind: "guest"` onto a roster that may hold only `foreign`.
  // „We could not load this" is the honest screen for that, and it is the
  // only thing that makes the claim above true.
  if (error || documentError || teamError || (!isLoading && !competition)) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState
              title="Soutěž se nepodařilo načíst"
              error={error ?? documentError ?? teamError}
              onRetry={() => {
                void refetch();
                void refetchDocument();
                void refetchTeam();
              }}
            />
          </Page.Content>
        </Page>
      </>
    );
  }
  // The roster must be in before the wizard mounts: edit mode seeds its
  // selection against it, and the inner component is keyed, so members
  // arriving later could not re-seed the state.
  if (
    isLoading ||
    !competition ||
    !document ||
    !index ||
    membersLoading ||
    teamLoading ||
    (editedId && entriesLoading)
  ) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (editedId && !entry) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Přihláška nenalezena" error={undefined} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }
  // A new MČR přihláška needs somebody registered to put on it. Without
  // this the picker opened empty and blamed a filter nobody had set. The
  // lists hide „Nová přihláška" under the same rule, but the route is
  // linkable, so the wizard checks for itself.
  // A team outside ČNSMS is refused at MČR as a team, whoever is on its
  // roster — a different sentence from „nobody is registered yet", and one
  // no registration would fix.
  if (!teamMayEnter(competition.type, teamIsCmf)) {
    return <NotForThisTeam teamId={teamId} compId={compId} crumbs={crumbs} />;
  }
  if (!editedId && needsSeasonRegistration(competition.type, hasRegisteredMember(members))) {
    return <RegistrationRequired teamId={teamId} compId={compId} crumbs={crumbs} />;
  }
  // An announced competition is visible, so its link reaches the wizard,
  // but nobody may file on it yet, the organizer included (managerGate).
  if (!editedId && competition.entry_phase === "not_open") {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <EmptyState
              title="Přihlašování zatím není otevřené"
              description="Přihlášky půjde podávat, až pořadatel soutěž zveřejní."
              action={
                <Link to={`/teams/${teamId}/souteze/${compId}`}>
                  <Button variant="primary">Zpět na přihlášky</Button>
                </Link>
              }
            />
          </Page.Content>
        </Page>
      </>
    );
  }
  // The server refuses it too (createGate); the route is linkable, so the
  // wizard says so instead of rendering a form that cannot be saved.
  if (!editedId && startList?.published && !canManageCompetition(viewer, compId)) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <EmptyState
              title="Novou přihlášku už podat nejde"
              description={ENTRY_LOCK_MESSAGES.start_list_published}
              action={
                <Link to={`/teams/${teamId}/souteze/${compId}`}>
                  <Button variant="primary">Zpět na přihlášky</Button>
                </Link>
              }
            />
          </Page.Content>
        </Page>
      </>
    );
  }
  // The route is linkable and a leader can hold the tab open across a
  // deadline, so the wizard guards itself rather than trusting the list
  // to have hidden „Upravit". Nothing here is a form the server would
  // accept, so it says so instead of rendering one.
  if (entry && !canEditEntry(entry.permissions)) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <EmptyState
              title="Přihlášku už upravit nejde"
              description={
                entry.permissions.lock_reason
                  ? ENTRY_LOCK_MESSAGES[entry.permissions.lock_reason]
                  : undefined
              }
              action={
                <Link to={`/teams/${teamId}/souteze/${compId}`}>
                  <Button variant="primary">Zpět na přihlášky</Button>
                </Link>
              }
            />
          </Page.Content>
        </Page>
      </>
    );
  }

  return (
    <EntryWizard
      // Remount when switching between entries so the initial state
      // reseeds from the edited record — and when „Další přihláška"
      // bumps the nonce, which is how the wizard starts over without
      // this component having to remember every field it would need to
      // clear by hand.
      key={`${entry?.id ?? "new"}:${restartNonce}`}
      teamId={teamId}
      competition={competition}
      document={document}
      index={index}
      members={members}
      teamIsCmf={teamIsCmf}
      teamCountry={team?.country_code || "CZ"}
      entry={entry}
      crumbs={crumbs}
      onRestart={() => setRestartNonce((n) => n + 1)}
    />
  );
}

// Its own component so the season and account hooks run only on this
// screen, not on every render of the wizard.
// A team outside ČNSMS cannot enter MČR at all: it is not a matter of
// registering anybody, so the screen offers the way back rather than a
// „Registrace do sezóny" that would be refused.
function NotForThisTeam({
  teamId,
  compId,
  crumbs,
}: {
  teamId: ID;
  compId: ID;
  crumbs: { label: string; href?: string }[];
}) {
  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Content>
          <EmptyState
            title="Na tuto soutěž se tým nepřihlašuje"
            description="Na kvalifikační kola a finále MČR se přihlašují jen týmy ČNSMS. Na pohárové soutěže se přihlásit můžete."
            action={
              <Link to={`/teams/${teamId}/souteze/${compId}`}>
                <Button>Zpět na přihlášky</Button>
              </Link>
            }
          />
        </Page.Content>
      </Page>
    </>
  );
}

function RegistrationRequired({
  teamId,
  compId,
  crumbs,
}: {
  teamId: ID;
  compId: ID;
  crumbs: { label: string; href?: string }[];
}) {
  const { data: me } = useMe();
  const { activeSeason } = useSeason();
  const { data: team } = useTeam(teamId);
  const access = seasonRegistrationAccess(activeSeason, me, teamId, team?.federation_id);
  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Content>
          <EmptyState
            title="Na tuto soutěž se zatím přihlásit nelze"
            description={
              "Na kvalifikační kola a finále MČR se může přihlásit jen tým s registrovanými členy. Tým zatím nemá v této sezóně nikoho registrovaného." +
              (access === "open" ? "" : ` ${SEASON_REGISTRATION_BLOCKED[access]}`)
            }
            action={
              access === "open" ? (
                <Link to={`/teams/${teamId}/registrace`}>
                  <Button variant="primary">Registrace do sezóny</Button>
                </Link>
              ) : (
                <Link to={`/teams/${teamId}/souteze/${compId}`}>
                  <Button>Zpět na přihlášky</Button>
                </Link>
              )
            }
          />
        </Page.Content>
      </Page>
    </>
  );
}

interface EntryWizardProps {
  teamId: ID;
  competition: Competition;
  document: RuleSetDocument;
  index: DocumentIndex;
  members: Membership[];
  /** Whether the team belongs to ČNSMS; decides which kind a competitor
   * added from here gets, and what the MČR refusal says. */
  teamIsCmf: boolean;
  /** The state the club represents, which is the likelier nationality of
   * somebody it adds here than „CZ" is. Still a default, not a claim: the
   * select is right there. */
  teamCountry: string;
  entry?: Entry;
  crumbs: { label: string; href?: string }[];
  /** Starts a fresh přihláška from the music step by remounting the
   * wizard — see the key in the parent. */
  onRestart: () => void;
}

function EntryWizard({
  teamId,
  competition,
  document,
  index,
  members,
  teamIsCmf,
  teamCountry,
  entry,
  crumbs,
  onRestart,
}: EntryWizardProps) {
  const navigate = useNavigate();
  const compId = competition.id;
  const t = useT();
  const { data: offer } = useCompetitionOffer(compId);
  // In Probíhá the organizer's přihláška is a live command (R25), which
  // answers with no entry: the wizard then returns to the list.
  const live = competition.status === "running" ? compId : undefined;
  const create = useCreateEntry(teamId, live);
  const update = useUpdateEntry(live);

  const editedCombo = entry ? index.combos.get(entry.discipline_category_id) : undefined;
  // Past the uzávěrka změn kategorie the kombinace is settled and only
  // the soutěžící are still the club's. The server's answer, never
  // worked out from the deadlines here.
  const categoryLocked = !!entry && !entry.permissions.can_change_category;
  // …so the wizard opens on the screen the leader came for. The step
  // strip keeps all three entries: renumbering „2. Soutěžící" to „1."
  // would break both the mental model and the e2e, and „Zpět" still
  // reaches step 0 as a read-only recap.
  const [step, setStep] = useState(categoryLocked ? 1 : 0);
  // The entry this wizard just created, so the music step has something
  // to upload against. Null while editing — an existing entry's music is
  // changed from the list, where it can be reached at any time.
  const [createdId, setCreatedId] = useState<ID | null>(null);
  const [familyCode, setFamilyCode] = useState(editedCombo?.discipline.family ?? "");
  const [disciplineId, setDisciplineId] = useState(
    editedCombo ? String(editedCombo.discipline.id) : "",
  );
  const [formationSizeId, setFormationSizeId] = useState(
    editedCombo ? String(editedCombo.category.formation_size_id) : "",
  );
  const [performanceClassId, setPerformanceClassId] = useState(
    editedCombo ? String(editedCombo.category.performance_class_id) : "",
  );
  const [memberIds, setMemberIds] = useState<ID[]>(() => {
    if (!entry) return [];
    // Reconcile against the roster: a competitor whose membership has
    // since closed would have no visible row, could not be unchecked,
    // and would silently ride along into the submit.
    const roster = new Set(members.map((m) => m.id));
    return entry.competitors
      .map((c) => c.team_membership_id)
      .filter((mid) => roster.has(mid));
  });
  const [memberQuery, setMemberQuery] = useState("");
  // "" is „všechny"; otherwise an AgeCategory id as a string, like every
  // other Select on this page.
  const [ageCategoryId, setAgeCategoryId] = useState("");

  const [guestOpen, setGuestOpen] = useState(false);
  const [guest, setGuest] = useState<{
    firstName: string;
    lastName: string;
    birthDate: Dayjs | null;
    countryCode: string;
  }>({ firstName: "", lastName: "", birthDate: null, countryCode: teamCountry });
  const addGuest = useAddTeamMember(teamId);

  // The roster, sorted the way a leader scans it (Czech collation by
  // surname), narrowed by the search box. Selection is independent of
  // the filter — hiding a row never unpicks the person.
  const sortedMembers = useMemo(() => {
    const collator = new Intl.Collator("cs", { sensitivity: "base" });
    return [...members].sort((a, b) =>
      collator.compare(
        `${a.person.last_name} ${a.person.first_name}`,
        `${b.person.last_name} ${b.person.first_name}`,
      ),
    );
  }, [members]);
  // MČR-type competitions admit only registered members, so guests are
  // not even offered; a pohárovka lists the whole roster.
  const eligibleMembers = useMemo(
    () =>
      competition?.type === "cup"
        ? sortedMembers
        : sortedMembers.filter((m) => m.kind === "registered"),
    [sortedMembers, competition?.type],
  );

  // What the competition vypisuje is decided for its whole series, and
  // classes are excluded by CODE — the same class exists once per class
  // table, and "does třída B run here" is one decision.
  const excludedCodes = new Set(offer?.excluded_class_codes ?? []);
  const excludedDisciplines = new Set(offer?.excluded_discipline_ids ?? []);
  const offered: ComboRef[] = [...index.combos.values()].filter(
    (r) =>
      !excludedDisciplines.has(r.discipline.id) &&
      !(r.performanceClass && excludedCodes.has(r.performanceClass.code)),
  );
  // The family axis appears only when the set actually has more than
  // one — a single-family set starts straight at the discipline.
  const familyCodes = [...new Set(offered.map((r) => r.discipline.family))];
  const multiFamily = familyCodes.length > 1;
  const byFamily =
    multiFamily && familyCode
      ? offered.filter((r) => r.discipline.family === familyCode)
      : offered;
  const byDiscipline = byFamily.filter(
    (r) => !disciplineId || r.discipline.id === disciplineId,
  );
  const byFormation = byDiscipline.filter(
    (r) => !formationSizeId || r.category.formation_size_id === formationSizeId,
  );
  const byClass = byFormation.filter(
    (r) =>
      !performanceClassId ||
      r.category.performance_class_id === performanceClassId,
  );

  const discipline = disciplineId
    ? document.disciplines.find((d) => d.id === disciplineId)
    : undefined;
  const ageTable = discipline ? index.ageTables.get(discipline.age_category_table_id) : undefined;
  const formation = formationSizeId
    ? index.formationSizes.get(formationSizeId)
    : undefined;

  // useCallback because the columns below are memoized on these, and
  // useTableSort re-sorts whenever the columns identity changes — an
  // ordinary closure would rebuild them on every keystroke in the search
  // box and re-sort the roster with them (AGENTS.md §5).
  const competitionDate = competition.date;
  const memberAge = useCallback(
    (m: Membership) => (ageTable ? ageOn(m.person.birth_date, ageTable, competitionDate) : null),
    [ageTable, competitionDate],
  );
  // The member's own natural band in the selected discipline's ladder —
  // informative only; the entry's category comes from the aggregate.
  //
  // The CATEGORY, not its name: the column sorts and filters on it, and
  // a name sorts „DĚTI" before „SENIOR" while the ladder knows better.
  const memberAgeCategory = useCallback(
    (m: Membership) => {
      const a = memberAge(m);
      if (a === null || !ageTable) return null;
      return (ageTable.categories ?? []).find((c) => ageCategoryMatches(c, a)) ?? null;
    },
    [memberAge, ageTable],
  );
  // From the sorted roster, so the summary reads in name order too.
  const pickedMembers = sortedMembers.filter((m) => memberIds.includes(m.id));
  const ages = pickedMembers
    .map((m) => memberAge(m))
    .filter((a): a is number => a !== null);
  const aggregated = formation && ages.length > 0 ? aggregateAge(ages, formation.age_aggregation) : null;

  // The age category is never chosen — it is computed from the picked
  // competitors: the youngest offered category that admits the
  // aggregated age. A younger group starting in an older category is
  // allowed and announced; an age over every offered band blocks.
  const axesChosen = !!disciplineId && !!formationSizeId && !!performanceClassId;
  const candidates = byClass
    .filter((r) => r.ageCategory)
    .sort((a, b) => a.ageCategory!.sort_order - b.ageCategory!.sort_order);
  const selected =
    axesChosen && aggregated !== null ? pickComboForAge(candidates, aggregated) : undefined;
  const chosenAgeCategory = selected?.ageCategory;
  // Every combo in byClass shares the picked class, so any row names it.
  const chosenClass = performanceClassId ? byClass[0]?.performanceClass : undefined;
  // The picked axes, one line — the roster card's subtitle. The
  // computed age category deliberately stays out of it; it has its own
  // line with the number that produced it.
  const comboBannerLabel = [discipline?.name, formation?.name, chosenClass?.name]
    .filter(Boolean)
    .join(" · ");
  // „Průměrný věk: 14,5 → JUNIOR MLADŠÍ" — a plain statement of fact,
  // phrased per the formation's aggregation rule and rendered in the
  // ActionBar with the rest of the step's status. Never an alert: alerts
  // are reserved for warnings.
  //
  // How this combination arrives at one age — „Věk nejstaršího" for a duo,
  // „Průměrný věk" for a formation, „Vlastní věk" for a solo. Named from the
  // one map so the wizard and the rule set cannot drift apart.
  const ageRule = formation ? AGE_AGGREGATION_LABELS[formation.age_aggregation] : null;
  const aggregatedLabel =
    aggregated !== null && ageRule
      ? `${ageRule}: ${formatAge(aggregated)}` +
        (chosenAgeCategory ? ` → ${chosenAgeCategory.name}` : "")
      : null;

  // The ladder the „Věková kategorie" filter offers, youngest first —
  // the whole ladder, not only the bands somebody happens to fall into,
  // so the options do not move around as the roster is filtered.
  const ageCategoryOptions = useMemo(
    () =>
      [...(ageTable?.categories ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    [ageTable],
  );

  // A category id chosen against a PREVIOUS discipline's ladder is not a
  // filter any more, so it stops being one.
  //
  // Derived rather than cleared in an effect: going back a step and
  // picking a discipline with a different age table would otherwise leave
  // the id in place, matching nobody — the picker would say „nikdo
  // neodpovídá filtru" while the select, whose value matches none of its
  // options, rendered as though nothing were filtered.
  const activeAgeCategoryId =
    ageCategoryId && ageCategoryOptions.some((c) => String(c.id) === ageCategoryId)
      ? ageCategoryId
      : "";

  // Search and věková kategorie, in one pass. Selection is independent of
  // both — hiding a row never unpicks the person, which is why the
  // controlled `memberIds` below is filtered against `next`, not against
  // what is on screen.
  const visibleMembers = useMemo(() => {
    const q = normalizeSearch(memberQuery);
    return eligibleMembers.filter((m) => {
      if (q && !normalizeSearch(`${m.person.first_name} ${m.person.last_name}`).includes(q)) {
        return false;
      }
      if (activeAgeCategoryId && memberAgeCategory(m)?.id !== activeAgeCategoryId) {
        return false;
      }
      return true;
    });
  }, [eligibleMembers, memberQuery, activeAgeCategoryId, memberAgeCategory]);

  // The roster picker's columns.
  //
  // Hoisted, because useTableSort re-sorts on the columns identity. Every
  // one of them is sortable: this is the screen where a leader works out
  // who fits a formace, and „kdo je ve které kategorii" and „kdo ještě
  // není registrovaný" are exactly the two questions it answers.
  const memberColumns: Column<Membership>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Jméno",
        sortable: true,
        // Surname first, like the roster it mirrors — a leader looks
        // people up by příjmení, and the cell reads the other way round
        // only because that is how a name is spoken.
        sortValue: (m) => `${m.person.last_name} ${m.person.first_name}`,
        tdClass: styles.colName,
        render: (m) => `${m.person.first_name} ${m.person.last_name}`,
      },
      {
        key: "age",
        // Name the reference date right in the header — „Věk k 31. 12.
        // 2027" — so the number is unambiguous.
        header: ageTable ? `Věk ${ageReferenceLabel(ageTable, competitionDate)}` : "Věk",
        numeric: true,
        sortable: true,
        // The number, not the „—" the cell prints when no discipline has
        // been picked yet: missing is null, so it sorts last both ways.
        sortValue: (m) => memberAge(m),
        tdClass: styles.colAge,
        render: (m) => memberAge(m) ?? "—",
      },
      {
        key: "category",
        header: "Kategorie",
        sortable: true,
        // The ladder's order, never the name: sorted as text „DĚTI" files
        // before „SENIOR" and „JUNIOR" before „KADET", which is the same
        // trap the soutěžní kategorie carry (AGENTS.md §"category order").
        //
        // And AGE breaks the tie inside a band, so the seniorky are
        // together AND in order rather than in whatever sequence the
        // roster happened to hold. Both halves are packed into one number
        // because `sortValue` takes a single comparable: the band is the
        // thousands and the age the units, so a band never interleaves
        // (nobody is 1000 years old) and a member the ladder cannot place
        // is `null`, which sorts last in both directions.
        //
        // The band comes from `memberAgeCategory`, the same function the
        // „Věková kategorie" filter uses — the sort and the filter cannot
        // disagree about which category somebody is in.
        sortValue: (m) => {
          const cat = memberAgeCategory(m);
          if (!cat) return null;
          return cat.sort_order * 1000 + (memberAge(m) ?? 0);
        },
        tdClass: styles.colCategory,
        render: (m) => {
          const cat = memberAgeCategory(m);
          return cat ? (
            <AgeBadge name={cat.name} color={cat.color} />
          ) : (
            <Text variant="muted">—</Text>
          );
        },
      },
      // „Registrovaný" answers a ČNSMS question, and nobody on a club
      // outside ČNSMS can hold that kind — the same empty stripe the team
      // roster drops for the same reason.
      ...(teamIsCmf
        ? ([
            {
              key: "registered",
              header: "Registrovaný",
              align: "right",
              sortable: true,
              // Presence, deliberately not null for a nečlen: null would sort
              // them last in both directions and make „kdo ještě není
              // registrovaný" unaskable, which is the question this is for.
              sortValue: (m) => (m.kind === "registered" ? 1 : 0),
              tdClass: styles.colRegistered,
              render: (m) => (
                <RegisteredTick registered={m.kind === "registered"} />
              ),
            },
          ] satisfies Column<Membership>[])
        : []),
    ],
    [ageTable, competitionDate, memberAge, memberAgeCategory, teamIsCmf],
  );

  // Local state, no URL params: the sort is a scan aid inside a wizard
  // step, and the wizard keeps its own step out of the URL too.
  const memberSort = useTableSort(visibleMembers, {
    columns: memberColumns,
    defaultSort: { key: "name", dir: "asc" },
  });

  // Past the uzávěrka změn kategorie the kombinace is HELD, not derived.
  //
  // The věková kategorie is the one axis nobody picks — it is computed
  // from whoever is on the entry — so re-deriving it here would turn
  // every roster swap into a category change. It would also be wrong in
  // the one direction that matters: the server admits a younger group in
  // an older category (rules.AgeCategoryAdmits binds only the upper
  // bound), so swapping a 18letá for a 13letá on a Senior přihláška is
  // legal, while `pickComboForAge` would answer „Junior" and refuse it.
  //
  // So the submitted category is the stored one, and the only thing that
  // can still go wrong is an age the stored category does not admit.
  const lockedAgeCategory = entry ? editedCombo?.ageCategory : undefined;
  const submittedCategoryId =
    categoryLocked && entry ? entry.discipline_category_id : selected?.category.id;
  const ageOverLockedCategory =
    categoryLocked &&
    !!lockedAgeCategory &&
    aggregated !== null &&
    !ageCategoryAdmits(lockedAgeCategory, aggregated);

  // Under lock the category on the přihláška is the one that counts, so
  // „nastoupí ve vyšší věkové kategorii" is measured against it rather
  // than against whatever the roster would have picked.
  const effectiveAgeCategory =
    categoryLocked && lockedAgeCategory ? lockedAgeCategory : chosenAgeCategory;
  const competingUp =
    !!effectiveAgeCategory &&
    aggregated !== null &&
    !ageCategoryMatches(effectiveAgeCategory, aggregated);
  // Only when the category is still being chosen: under lock there is
  // nothing to pick, and an age above every offered band is reported as
  // being above the locked one instead.
  const noCategoryForAge = aggregated !== null && axesChosen && !selected && !categoryLocked;
  const headcountOff =
    formation && memberIds.length > 0 && !headcountFits(formation, memberIds.length);
  const isCup = competition.type === "cup";
  const ineligiblePicked = pickedMembers.some(
    (m) => !competitorMayEnter(competition.type, m.kind),
  );

  const writeError = create.error || update.error;
  const isWriting = create.isPending || update.isPending;
  const serverRows = entryErrorRows(writeError);
  const fieldError = (field: string) => serverRows.find((r) => r.field === field)?.message;
  const competitorMessages = serverRows.filter((r) => r.field === "competitor");
  const memberName = (mid: ID) => {
    const m = members.find((x) => x.id === mid);
    return m ? `${m.person.first_name} ${m.person.last_name}` : "—";
  };

  // The picker runs the library's selection in CONTROLLED mode: the picked
  // set is a form value seeded from the server (see memberIds above), not
  // state the table may own. onChange keeps the previous order and appends
  // new picks, so the ids submitted stay in the order they were chosen.
  //
  // withToggleAll: false because a headcount-limited picker has no valid
  // "select all" — for any formation smaller than the roster it could only
  // ever overshoot the bound, and the control would exist to be refused.
  //
  // The ROWS above the bound are disabled for the same reason, via
  // `selection.selectable` (0.30.0). Note the first half of the predicate:
  // somebody already picked stays toggleable, or a full formation could
  // never be changed, only cleared. The cap is still REPORTED as well — the
  // headcount alert and a blocked „Pokračovat" — because a disabled
  // checkbox says „not this one", not „you already have three".
  const pickedKeys = useMemo(() => new Set(memberIds), [memberIds]);
  const memberSelection = useRowSelection<Membership>(visibleMembers, (m) => m.id, {
    value: pickedKeys,
    onChange: (next) =>
      setMemberIds((prev) => {
        // rowKey is m.id, so every key here is a membership id; the hook's
        // key type is the wider string | number, hence the narrowing.
        const picked = [...next].filter((id): id is ID => typeof id === "string");
        return [
          ...prev.filter((id) => next.has(id)),
          ...picked.filter((id) => !prev.includes(id)),
        ];
      }),
    withToggleAll: false,
  });

  // The upper bound of the picked formation, when it has one. Sólo admits
  // one, duo/trio three; a formation with no maximum (`max_count` null,
  // e.g. „min. 8") leaves every row selectable.
  const maxCompetitors = formation?.max_count ?? null;

  // The one definition of the alert region (rendered in two places).
  function renderAlerts() {
    return (
      <>
        {/* In the alert region rather than inside the card: the card
            now states the kombinace, and the reason it can only state
            it belongs with everything else that narrows this screen. */}
        {step === 0 && categoryLocked && (
          <Alert variant="warn">
            Uzávěrka změn kategorie proběhla, kombinaci už změnit nejde. Soutěžící
            měnit můžete do uzávěrky změn soutěžících.
          </Alert>
        )}
        {step >= 1 && (
          <>
            {/* First, because it is the only one the leader cannot fix
                by reading further: everything else on this step is about
                the roster being wrong, and this one is about the roster
                being right for a different kategorie than the one on the
                přihláška. */}
            {ageOverLockedCategory && lockedAgeCategory && (
              <Alert variant="err">
                Věkovou kategorii už měnit nejde; uzávěrka změn kategorie proběhla.
                Přihláška je v kategorii {lockedAgeCategory.name}, ale z vybraných
                soutěžících vychází věk {formatAge(aggregated!)}, který do ní nepatří.
                Vyberte soutěžící, kteří se do kategorie {lockedAgeCategory.name} vejdou,
                nebo se ozvěte pořadateli soutěže.
              </Alert>
            )}
            {noCategoryForAge && (
              <Alert variant="warn">
                Pro vypočtený věk {formatAge(aggregated!)} není v této kombinaci vypsána
                žádná věková kategorie; věk přesahuje všechny vypsané.
              </Alert>
            )}
            {/* Not while the kategorie has moved: „nastoupí ve vyšší
                věkové kategorii" is a note about a start that is going to
                happen, and above it would be a warning that this one
                cannot. */}
            {competingUp && effectiveAgeCategory && (
              <Alert variant="info">
                Vypočtený věk {formatAge(aggregated!)} je pod rozsahem kategorie{" "}
                {effectiveAgeCategory.name}, soutěžící nastoupí ve vyšší věkové kategorii.
              </Alert>
            )}
            {headcountOff && formation && (
              <Alert variant="warn">
                Formace {formation.name} vyžaduje {headcountBound(formation)} soutěžících.
              </Alert>
            )}
            {ineligiblePicked && (
              <Alert variant="warn">
                Na soutěže MČR smí jen řádně registrovaní členové; nečlen může na
                pohárovky.
              </Alert>
            )}
          </>
        )}
        {step === 1 && addGuest.error && (
          <Alert variant="err">{apiErrorMessage(addGuest.error, t)}</Alert>
        )}
        {step === 2 && writeError && serverRows.length === 0 && (
          <Alert variant="err">{apiErrorMessage(writeError, t)}</Alert>
        )}
        {step === 2 && fieldError("age_category") && (
          <Alert variant="err">{fieldError("age_category")}</Alert>
        )}
        {step === 2 && fieldError("team_membership_ids") && (
          <Alert variant="err">{fieldError("team_membership_ids")}</Alert>
        )}
        {step === 2 && competitorMessages.length > 0 && (
          <Alert variant="err">
            {competitorMessages.map((r) => (
              <div key={`${r.team_membership_id}-${r.message}`}>
                {r.team_membership_id ? `${memberName(r.team_membership_id)}: ` : ""}
                {r.message}
              </div>
            ))}
          </Alert>
        )}
      </>
    );
  }

  const submitGuest = async () => {
    if (!guest.firstName.trim() || !guest.lastName.trim() || !guest.birthDate) return;
    try {
      const created = await addGuest.mutateAsync({
        person: {
          first_name: guest.firstName.trim(),
          last_name: guest.lastName.trim(),
          country_code: guest.countryCode,
          birth_date: toApiDate(guest.birthDate) ?? "",
          birth_number: "",
          street: "",
          city: "",
          postal_code: "",
          phone: "",
        },
        roles: ["athlete"],
        // The team's federation decides the kind: a ČNSMS club brings a
        // nečlen to a pohárovka, a foreign club brings its own member.
        kind: teamIsCmf ? "guest" : "foreign",
      });
      setMemberIds((ids) => [...ids, created.id]);
      setGuest({ firstName: "", lastName: "", birthDate: null, countryCode: teamCountry });
      setGuestOpen(false);
    } catch {
      // Rendered from addGuest.error below.
    }
  };

  const submit = async () => {
    // Under lock this is the STORED category, so a roster swap sends the
    // kombinace back unchanged and the server sees an edit it allows.
    // Re-deriving it here is what would turn a legal swap into a refused
    // category change.
    if (!submittedCategoryId) return;
    try {
      if (entry) {
        await update.mutateAsync({
          id: entry.id,
          discipline_category_id: submittedCategoryId,
          team_membership_ids: memberIds,
        });
      } else {
        const created = await create.mutateAsync({
          competition_id: competition.id,
          discipline_category_id: submittedCategoryId,
          team_membership_ids: memberIds,
        });
        if (usesOwnMusic && !created) {
          // A live command answers with no entry; the recording waits for
          // the list of přihlášky.
          notifySuccess("Přihláška je podaná. Hudbu nahrajete u přihlášky v seznamu.");
        }
        if (usesOwnMusic && created) {
          // Submitted. Offer the recording while the leader is still
          // here rather than making them find the entry again.
          setCreatedId(created.id);
          setStep(MUSIC_STEP);
          return;
        }
      }
      navigate(`/teams/${teamId}/souteze/${compId}`);
    } catch {
      // Errors render on the summary step.
    }
  };

  // A step with a known-bad state cannot be left: the live checks
  // mirror hard server rules, so letting the leader continue would only
  // postpone the same refusal to the submit.
  const competitorsBlocked =
    memberIds.length === 0 ||
    !!headcountOff ||
    ineligiblePicked ||
    ageOverLockedCategory ||
    // Under lock the category is held, so there is nothing to pick and
    // `selected` is not needed to submit.
    (!selected && !categoryLocked);

  // The one-line version of why the step will not let go, for the sticky
  // bar; the alert region above carries the full sentence. Same order the
  // condition above tests in, so the bar names the first thing to fix.
  const blockedReason = !competitorsBlocked
    ? null
    : memberIds.length === 0
      ? "Zatím nikdo není vybraný."
      : headcountOff && formation
        ? `Počet neodpovídá formaci ${formation.name} (${headcountBound(formation)}).`
        : ineligiblePicked
          ? "Nečlen nesmí na soutěže MČR."
          : ageOverLockedCategory
            ? "Věkovou kategorii už měnit nejde."
            : "Pro vypočtený věk není vypsaná žádná kategorie.";
  // Red rather than amber, and only for this one. Every other reason on
  // this step is a roster the leader can go on picking until it fits;
  // this one is a door that has already closed, and it may not be
  // fixable from here at all.
  const blockedVariant = ageOverLockedCategory ? "err" : "warn";
  const canContinue =
    step === 0 ? axesChosen && byClass.length > 0 : !competitorsBlocked;

  // A discipline the club does not upload for takes no music step: the
  // ČNSMS recording is the admin's to supply, and improvizace has no file
  // at all. Written as `=== "own"` rather than `!== "federation"` so a
  // future source cannot quietly grow a step that uploads nothing.
  //
  // Keyed on the CHOSEN discipline, and undefined counts as "not yet":
  // the step strip used to promise a „4. Hudba" from the first screen,
  // before anything was picked, and then take it away again if the
  // leader chose a federation discipline. A step is either coming or it
  // is not on the list.
  const usesOwnMusic = !entry && discipline?.music_source === "own";
  const stepLabels = usesOwnMusic ? [...STEP_LABELS, "Hudba"] : STEP_LABELS;
  // Music is not part of the přihláška: it is owed by the uzávěrka hudby,
  // a separate instant, and the leader should know that before submitting
  // rather than read a fourth step as one more thing required now. The
  // uzávěrka hudby is the one deadline a club page may compare itself
  // (e2e/specs/prihlaska/contract.spec.ts).
  const musicDeadline = competition.effective_music_deadline;
  const musicOpen = !musicDeadline || musicDeadline.isAfter(dayjs());
  const musicDaysLeft = musicDeadline ? daysLeftLabel(musicDeadline) : null;

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title={entry ? "Upravit přihlášku" : "Nová přihláška"}
          subtitle={`${competition.name} · ${formatCompetitionDates(competition)}`}
          actions={
            <Link to={`/teams/${teamId}/souteze/${compId}`}>
              <Button variant="danger" weight="outline">
                Zrušit
              </Button>
            </Link>
          }
        />
        <Page.Content stack gap="lg">
          {/* Step strip. */}
          <div className={styles.steps}>
            {stepLabels.map((label, i) => (
              <span key={label} className={styles.step}>
                {/* Lucide glyphs rather than the "→"/"✓" characters — see
                    docs/mockups/DESIGN.md §3. */}
                {i > 0 && (
                  <Text variant="muted">
                    <ChevronRight size={14} aria-hidden="true" />
                  </Text>
                )}
                {i === step ? (
                  <Badge variant="info">{`${i + 1}. ${label}`}</Badge>
                ) : (
                  <Text variant="muted">
                    <span className={styles.stepDone}>
                      {i < step && <Check size={14} aria-hidden="true" />}
                      {`${i + 1}. ${label}`}
                    </span>
                  </Text>
                )}
              </span>
            ))}
          </div>

          {step === 0 && (
            <Card>
              <Card.Header
                title={categoryLocked ? "Kombinace" : "Vyberte kombinaci"}
                subtitle={categoryLocked ? "Po uzávěrce změn kategorie" : undefined}
                subtitlePlacement="right"
              />
              <Card.Content>
                <FormStack>
                  {/* Past the uzávěrka změn kategorie the kombinace is
                      STATED rather than offered. Not a row of disabled
                      buttons: a disabled column reads as „temporarily
                      unavailable" and still costs a click to find out,
                      where a DescriptionList says the fact — and it is the
                      same primitive the deadline card two screens back
                      uses for the same job. */}
                  {categoryLocked && editedCombo ? (
                    <DescriptionList>
                      {multiFamily && (
                        <DescriptionList.Item label="Skupina disciplín">
                          {familyName(document.families, editedCombo.discipline.family)}
                        </DescriptionList.Item>
                      )}
                      <DescriptionList.Item label="Disciplína">
                        {editedCombo.discipline.name}
                      </DescriptionList.Item>
                      <DescriptionList.Item label="Formace">
                        {editedCombo.formationSize
                          ? `${editedCombo.formationSize.name} (${headcountBound(editedCombo.formationSize)})`
                          : "—"}
                      </DescriptionList.Item>
                      <DescriptionList.Item label="Výkonnostní třída">
                        {editedCombo.performanceClass?.name ?? "—"}
                      </DescriptionList.Item>
                    </DescriptionList>
                  ) : (
                  <>
                  {/* Pick-columns instead of dropdowns: each column shows
                      only the variants the previous picks allow, so an
                      impossible combination cannot even be clicked. The
                      family column leads, and only when the set has more
                      than one family. */}
                  <div
                    className={styles.pickGrid}
                    style={{ "--pick-cols": multiFamily ? 4 : 3 } as CSSProperties}
                  >
                    {multiFamily && (
                      <div className={styles.pickColumn}>
                        <FormSectionLabel>Skupina disciplín</FormSectionLabel>
                        {familyCodes.map((code) => (
                          <Button
                            key={code}
                            block
                            size="sm"
                            variant="primary"
                            weight={familyCode === code ? "solid" : "outline"}
                            onClick={() => {
                              setFamilyCode(code);
                              setDisciplineId("");
                              setFormationSizeId("");
                              setPerformanceClassId("");
                            }}
                          >
                            {familyName(document.families, code)}
                          </Button>
                        ))}
                      </div>
                    )}
                    <div className={styles.pickColumn}>
                      <FormSectionLabel>Disciplína</FormSectionLabel>
                      {multiFamily && !familyCode ? (
                        <Text variant="muted">Nejdřív vyberte skupinu.</Text>
                      ) : (
                        uniqueBy(byFamily, (r) => r.discipline.id).map((r) => (
                          <Button
                            key={r.discipline.id}
                            block
                            size="sm"
                            variant="primary"
                            weight={
                              disciplineId === String(r.discipline.id) ? "solid" : "outline"
                            }
                            onClick={() => {
                              setDisciplineId(String(r.discipline.id));
                              setFormationSizeId("");
                              setPerformanceClassId("");
                            }}
                          >
                            {r.discipline.name}
                          </Button>
                        ))
                      )}
                    </div>
                    <div className={styles.pickColumn}>
                      <FormSectionLabel>Formace</FormSectionLabel>
                      {!disciplineId ? (
                        <Text variant="muted">Nejdřív vyberte disciplínu.</Text>
                      ) : (
                        uniqueBy(byDiscipline, (r) => r.category.formation_size_id).map((r) =>
                          r.formationSize ? (
                            <Button
                              key={r.formationSize.id}
                              block
                              size="sm"
                              variant="primary"
                              weight={
                                formationSizeId === String(r.formationSize.id)
                                  ? "solid"
                                  : "outline"
                              }
                              onClick={() => {
                                setFormationSizeId(String(r.formationSize!.id));
                                setPerformanceClassId("");
                              }}
                            >
                              {r.formationSize.name} ({headcountBound(r.formationSize)})
                            </Button>
                          ) : null,
                        )
                      )}
                    </div>
                    <div className={styles.pickColumn}>
                      <FormSectionLabel>Výkonnostní třída</FormSectionLabel>
                      {!formationSizeId ? (
                        <Text variant="muted">Nejdřív vyberte formaci.</Text>
                      ) : (
                        uniqueBy(byFormation, (r) => r.category.performance_class_id).map((r) =>
                          r.performanceClass ? (
                            <Button
                              key={r.performanceClass.id}
                              block
                              size="sm"
                              variant="primary"
                              weight={
                                performanceClassId === String(r.performanceClass.id)
                                  ? "solid"
                                  : "outline"
                              }
                              onClick={() =>
                                setPerformanceClassId(String(r.performanceClass!.id))
                              }
                            >
                              {r.performanceClass.name}
                            </Button>
                          ) : null,
                        )
                      )}
                    </div>
                  </div>
                  </>
                  )}
                  {/* Still true, and still the explanation for a category
                      the summary will name that nobody picked. */}
                  {axesChosen && (
                    <Text variant="muted">
                      Věková kategorie se nevybírá; spočítá se z vybraných soutěžících
                      {ageTable ? ` (věk ${ageReferenceLabel(ageTable, competition.date)})` : ""}.
                      Vypsané: {candidates.map((r) => r.ageCategory!.name).join(", ") || "—"}.
                    </Text>
                  )}
                </FormStack>
              </Card.Content>
            </Card>
          )}

          {step === 1 && (
            <Card>
              <Card.Header title="Vyberte soutěžící" subtitle={comboBannerLabel || undefined} />
              {/* The card's own toolbar band, not a FilterPanel: being a surface
                  is FilterPanel's whole job, so nested in a card it draws a frame
                  within a frame, and stacked above one it draws a second frame on
                  the first. The band has the card's own rule and a muted fill
                  instead. `Card.Toolbar` renders its children verbatim — it is a
                  place, not a kind of content — so the row is explicit, and with
                  it `wide` means what it means in a page-level filter bar.

                  („Vlastní věk: 18 → SENIOR" used to sit in the header; it is in
                  the ActionBar now, with the rest of the step's status.) */}
              <Card.Toolbar>
                <FilterRow>
                  <FilterField wide>
                    <Input
                      label="Hledat soutěžící"
                      type="search"
                      placeholder="Jméno nebo příjmení…"
                      value={memberQuery}
                      onChange={(e) => setMemberQuery(e.target.value)}
                      // `clearable` reads `value` to decide whether there is
                      // anything to clear, so it replaces both the chip and the
                      // `memberQuery ? … : undefined` guard around it. It needs
                      // a controlled input and an `onClear`; both pairings are
                      // enforced by `no-dead-companion-props`.
                      clearable
                      onClear={() => setMemberQuery("")}
                    />
                  </FilterField>
                  {/* Only once a discipline is picked: without one there is no
                      ladder, so the control would offer nothing and the column
                      beside it reads „—" on every row. */}
                  {ageCategoryOptions.length > 0 && (
                    <FilterField>
                      <Select
                        label="Věková kategorie"
                        value={activeAgeCategoryId}
                        onChange={(e) => setAgeCategoryId(e.target.value)}
                      >
                        <option value="">Všechny</option>
                        {ageCategoryOptions.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name}
                          </option>
                        ))}
                      </Select>
                    </FilterField>
                  )}
                </FilterRow>
              </Card.Toolbar>
              <Card.Content padding="none">
                <Table<Membership>
                  {...memberSort.tableProps}
                  rowKey={(m) => m.id}
                  selection={{
                    ...memberSelection.selection,
                    label: (m) => `${m.person.first_name} ${m.person.last_name}`,
                    // Already picked stays clickable so a full formation can be
                    // changed; everything else closes once the bound is reached.
                    selectable: (m) =>
                      maxCompetitors == null ||
                      pickedKeys.has(m.id) ||
                      memberIds.length < maxCompetitors,
                  }}
                  empty={
                    // Two different empties: nobody to pick at all, and a
                    // filter hiding everybody. Only the second is the
                    // leader's filter to blame.
                    eligibleMembers.length === 0 ? (
                      <EmptyState
                        title="Na soupisce zatím nikdo není"
                        description={
                          isCup
                            ? "Přidejte neregistrovaného člena níže."
                            : "Na soutěže MČR mohou jen registrovaní členové a tým zatím nikoho registrovaného nemá."
                        }
                      />
                    ) : (
                      <EmptyState
                        title="Nikdo neodpovídá filtru"
                        description="Zkuste jiné jméno nebo jinou věkovou kategorii. Vybraní soutěžící zůstávají vybraní, i když je filtr skryje."
                      />
                    )
                  }
                  columns={memberColumns}
                />
              </Card.Content>
              {isCup && (
                <Card.Footer>
                  <FormStack>
                    {!guestOpen && (
                      <Button variant="primary" block onClick={() => setGuestOpen(true)}>
                        {teamIsCmf ? "Přidat neregistrovaného člena" : "Přidat soutěžícího"}
                      </Button>
                    )}
                    {guestOpen && (
                      <FormStack>
                        <ResponsiveGrid columns={2}>
                          <Input
                            label="Jméno"
                            required
                            value={guest.firstName}
                            onChange={(e) => setGuest({ ...guest, firstName: e.target.value })}
                          />
                          <Input
                            label="Příjmení"
                            required
                            value={guest.lastName}
                            onChange={(e) => setGuest({ ...guest, lastName: e.target.value })}
                          />
                        </ResponsiveGrid>
                        <ResponsiveGrid columns={2}>
                          <DatePicker
                            label="Datum narození"
                            value={guest.birthDate}
                            onChange={(v) => setGuest({ ...guest, birthDate: v })}
                          />
                          <Select
                            label="Státní příslušnost"
                            value={guest.countryCode}
                            onChange={(e) => setGuest({ ...guest, countryCode: e.target.value })}
                          >
                            {COUNTRY_OPTIONS.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Select>
                        </ResponsiveGrid>
                        <div className={`${styles.buttonRow} ${styles.buttonRowEnd}`}>
                          <Button onClick={() => setGuestOpen(false)}>
                            Zrušit
                          </Button>
                          <Button
                            variant="primary"
                            loading={addGuest.isPending}
                            disabled={
                              !guest.firstName.trim() || !guest.lastName.trim() || !guest.birthDate
                            }
                            onClick={submitGuest}
                          >
                            Přidat na soupisku
                          </Button>
                        </div>
                      </FormStack>
                    )}
                  </FormStack>
                </Card.Footer>
              )}
            </Card>
          )}

          {step === 2 && (
            // The check-screen. Deliberately bespoke instead of a
            // DescriptionList + Table: one glance answers "what, who, in
            // which category" — the headline is the result-list key.
            <Card>
              <Card.Content>
                <div className={styles.summary}>
                  <div className={styles.summaryHead}>
                    <div className={styles.summaryDiscipline}>{discipline?.name ?? "—"}</div>
                    <div className={styles.summaryAxes}>
                      {[formation?.name, chosenAgeCategory?.name, chosenClass?.name]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>

                  <div className={styles.summaryFacts}>
                    <div className={styles.fact}>
                      <span className={styles.factLabel}>Soutěž</span>
                      <span className={styles.factValue}>{competition.name}</span>
                      <span className={styles.factMeta}>{formatCompetitionDates(competition)}</span>
                    </div>
                    {formation && (
                      <div className={styles.fact}>
                        <span className={styles.factLabel}>Formace</span>
                        <span className={styles.factValue}>{formation.name}</span>
                        <span className={styles.factMeta}>
                          {headcountBound(formation)}, přihlášeno {pickedMembers.length}
                        </span>
                      </div>
                    )}
                    {aggregated !== null && (
                      <div className={styles.fact}>
                        <span className={styles.factLabel}>
                          {ageTable ? `Věk ${ageReferenceLabel(ageTable, competition.date)}` : "Věk"}
                        </span>
                        <span className={styles.factValue}>
                          {ageRule ? `${ageRule}: ${formatAge(aggregated)}` : formatAge(aggregated)}
                        </span>
                        {chosenAgeCategory && (
                          <span className={styles.factMeta}>
                            <AgeBadge
                              name={chosenAgeCategory.name}
                              color={chosenAgeCategory.color}
                            />
                          </span>
                        )}
                      </div>
                    )}
                    {selected?.category.time_max_seconds != null && (
                      <div className={styles.fact}>
                        <span className={styles.factLabel}>Časový limit</span>
                        <span className={styles.factValue}>
                          {formatSeconds(selected.category.time_min_seconds)}–
                          {formatSeconds(selected.category.time_max_seconds)}
                        </span>
                      </div>
                    )}
                    {usesOwnMusic && (
                      <div className={styles.fact}>
                        <span className={styles.factLabel}>Hudba</span>
                        <span className={styles.factValue}>
                          {musicOpen ? "Nahrajete po podání" : "Nahraje pořadatel"}
                        </span>
                        {musicOpen && musicDeadline && (
                          <span className={styles.factMeta}>
                            do {formatDateTime(musicDeadline)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <span className={styles.factLabel}>Soutěžící ({pickedMembers.length})</span>
                    <p className={styles.competitorLine}>
                      {pickedMembers
                        .map((m) => {
                          const a = memberAge(m);
                          return `${m.person.first_name} ${m.person.last_name}${a !== null ? ` (${a})` : ""}`;
                        })
                        .join(", ")}
                    </p>
                  </div>
                </div>
              </Card.Content>
            </Card>
          )}

          {step === MUSIC_STEP && createdId !== null && (
            <>
              {/* Above the card, not inside it: the alert reports on the
                  step that just finished, while the card is the one still
                  open. Nested, the two boxes read as a single panel. */}
              <Alert variant="ok" title="Přihláška je podaná">
                {musicOpen
                  ? `Hudbu nemusíte nahrávat hned. Stačí ji nahrát${
                      musicDeadline
                        ? ` do uzávěrky hudby ${formatDateTime(musicDeadline)}${
                            musicDaysLeft ? ` (${musicDaysLeft})` : ""
                          }`
                        : " kdykoliv později"
                    }; najdete ji v přehledu přihlášek.`
                  : "Uzávěrka hudby už proběhla. Hudbu k přihlášce nahraje pořadatel soutěže."}
              </Alert>
              <Card>
                <Card.Header
                  title="Hudba"
                  subtitle={
                    musicOpen && musicDeadline
                      ? `Nepovinné · do ${formatDateTime(musicDeadline)}`
                      : "Nepovinné"
                  }
                  subtitlePlacement="right"
                />
                <Card.Content>
                  <MusicStep entryId={createdId} disabled={!musicOpen} />
                </Card.Content>
              </Card>
            </>
          )}

          {/* The alert region — every warning and server error lands
              here, never inside the cards: once, above the continue
              button, so the reason a step is blocked sits next to the
              disabled action. */}
          {renderAlerts()}

          {/* The same lib bar the season-registration roster uses: the state
              of the work on the left, the actions that end it on the right,
              pinned so a soupiska longer than the viewport cannot push the
              „Pokračovat" button — or the sentence saying why it is
              disabled — off the screen. `announce` stays on here: nothing in
              this slot opens a popover, and „Vybráno N z M" plus the reason a
              step is blocked is what a polite region is for. */}
          <ActionBar
            aria-label="Krok průvodce"
            /* Two pieces (count + blocked reason), not a sentence. */
            startLayout="row"
            start={
              <>
                {step === 1 && (
                  <Text variant="muted">
                    Vybráno {memberIds.length} z {eligibleMembers.length}
                  </Text>
                )}
                {/* „Vlastní věk: 18 → SENIOR" — the computed age and the
                    category it lands in. It belongs beside the count rather
                    than in the card header: both are the state of this step,
                    it changes as competitors are ticked, and the bar is
                    pinned while the roster scrolls. A plain statement of
                    fact, never an alert. */}
                {step === 1 && aggregatedLabel && <Text variant="muted">{aggregatedLabel}</Text>}
                {/* Short enough to sit on one line; the full explanation stays
                    in the alert region above. Deliberately worded not to repeat
                    the alerts' „vyžaduje" — e2e matches that word by text. */}
                {step >= 1 && step < MUSIC_STEP && blockedReason && (
                  <Badge variant={blockedVariant}>{blockedReason}</Badge>
                )}
              </>
            }
          >
            {/* No way back from the music step: the přihláška is already
                submitted, and offering „Zpět" to a screen that would
                submit it again is a trap. */}
            {step > 0 && step < MUSIC_STEP && (
              <Button onClick={() => setStep(step - 1)}>
                Zpět
              </Button>
            )}
            {step < 2 ? (
              <Button variant="primary" disabled={!canContinue} onClick={() => setStep(step + 1)}>
                Pokračovat
              </Button>
            ) : step === MUSIC_STEP ? (
              // Two ways onward, because a club rarely enters one
              // přihláška: „Další přihláška" is the primary because it is
              // the likelier next move, and it remounts the wizard rather
              // than clearing a dozen fields by hand.
              <>
                <Button
                  onClick={() => navigate(`/teams/${teamId}/souteze/${compId}`)}
                >
                  Zpět na přehled
                </Button>
                <Button variant="primary" onClick={onRestart}>
                  Další přihláška
                </Button>
              </>
            ) : (
              <Button variant="primary" loading={isWriting} disabled={!canContinue} onClick={submit}>
                {entry ? "Uložit změny" : "Podat přihlášku"}
              </Button>
            )}
          </ActionBar>
        </Page.Content>
      </Page>
    </>
  );
}

// MusicStep is a component rather than an inline block because the
// upload hook is keyed to an entry id that does not exist until the
// wizard has submitted — a hook called before then would have nothing to
// point at.
function MusicStep({ entryId, disabled }: { entryId: ID; disabled: boolean }) {
  const upload = useUploadEntryMusic(entryId);
  const [music, setMusic] = useState<EntryMusic | null>(null);
  return (
    <MusicPanel
      src={entryMusicUrl(entryId)}
      music={music}
      disabled={disabled}
      upload={upload}
      onUploaded={setMusic}
    />
  );
}

function uniqueBy<T>(rows: T[], key: (row: T) => ID): T[] {
  const seen = new Set<ID>();
  const out: T[] = [];
  for (const r of rows) {
    const k = key(r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

// normalizeSearch folds case and diacritics so "novak" finds Nováková.
function normalizeSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

// The bounds are rule-set time limits, so they go through the rule set's own
// formatter rather than a fourth spelling of m:ss — which is how the wizard
// and the kombinace it is quoting end up disagreeing about a leading zero.
// Only the missing case is local: an absent bound means the kombinace states
// none, and „?" says that where the shared „—" placeholder would be read as
// part of the dash-separated range it sits in.
function formatSeconds(v?: number | null): string {
  return v == null ? "?" : formatTimeBound(v);
}
